import { createSlice } from '@reduxjs/toolkit';
import { orderDeliveryTypes } from '../../constants/order.constant';
import { calculateBasketPrices } from '../../utils/calculateBasketPrices';

const initialBasket = {
  basketStore: null,
  deliveryType: orderDeliveryTypes[0],
  items: [],
  tipType: 'rate',
  tip: 0,
  tiprate: 0,
  discountcode: '',
  prices: null,
  // True while the optimistic client recalc is showing and the server's
  // authoritative CalculateForCustomer response hasn't landed yet. The
  // client can't price extra fees (definitions live server-side), so
  // fee-inclusive totals render as "updating" while this is set.
  pricesStale: false,
  note: '',
  table: '',
  tableAlert: false,
  currentOrder: null,
  // Scheduling
  schedulingType: 'asap', // "asap" | "schedule"
  pickuptimeutc: null,
  // Guest info
  phone: '',
  name: '',
  search: '',
};

export const basketSlice = createSlice({
  name: 'basket',
  initialState: initialBasket,
  reducers: {
    refreshBasket: () => {
      return initialBasket;
    },
    setBasketStore: (state, action) => {
      state.basketStore = action.payload;
    },
    addToCart: (state, action) => {
      const itemInCart = state.items.find(
        (item) => item.basketId === action.payload.basketId
      );
      if (itemInCart) {
        // If same item exists, increment qty and recalculate total
        const unitPrice = itemInCart.total / itemInCart.qty;
        itemInCart.qty++;
        itemInCart.total = unitPrice * itemInCart.qty;
      } else {
        state.items.push({ ...action.payload });
      }
      // Update prices client-side
      const newPrices = calculateBasketPrices(state);
      if (newPrices) {
        state.prices = newPrices;
        state.pricesStale = true;
      }
    },
    incrementQty: (state, action) => {
      const item = state.items.find((item) => item.basketId === action.payload);
      const diff = item.total / item.qty;
      item.qty++;
      item.total = item.total + diff;
      // Update prices client-side
      const newPrices = calculateBasketPrices(state);
      if (newPrices) {
        state.prices = newPrices;
        state.pricesStale = true;
      }
    },
    decrementQty: (state, action) => {
      const item = state.items.find((item) => item.basketId === action.payload);
      const diff = item.total / item.qty;
      if (item.qty === 1) {
        item.qty = 1;
      } else {
        item.qty--;
        item.total = item.total - diff;
      }
      // Update prices client-side
      const newPrices = calculateBasketPrices(state);
      if (newPrices) {
        state.prices = newPrices;
        state.pricesStale = true;
      }
    },
    updateItem: (state, action) => {
      const updatedItems = state.items.map((item) => {
        if (item.basketId === action.payload.basketId) {
          return { ...item, ...action.payload };
        }
        return item;
      });
      state.items = updatedItems;
      // Update prices client-side
      const newPrices = calculateBasketPrices(state);
      if (newPrices) {
        state.prices = newPrices;
        state.pricesStale = true;
      }
    },
    removeItem: (state, action) => {
      const removedItems = state.items.filter(
        (item) => item.basketId !== action.payload
      );
      state.items = removedItems;
      // If basket is empty, clear discount code and prices
      if (removedItems.length === 0) {
        state.discountcode = '';
        state.prices = null;
        state.pricesStale = false;
      } else {
        // Update prices client-side
        const newPrices = calculateBasketPrices(state);
        if (newPrices) {
          state.prices = newPrices;
          state.pricesStale = true;
        }
      }
    },
    setDeliveryType: (state, action) => {
      state.deliveryType = action.payload;
      state.pricesStale = true;
    },
    setDiscountCode: (state, action) => {
      state.discountcode = action.payload;
      state.pricesStale = true;
    },
    setBasketPrices: (state, action) => {
      state.prices = action.payload;
      state.pricesStale = false;
    },
    // Escape hatch when the server recalc fails: stop the "updating"
    // treatment rather than pulsing forever over numbers that won't settle.
    setPricesSettled: (state) => {
      state.pricesStale = false;
    },
    setNote: (state, action) => {
      state.note = action.payload;
    },
    setTip: (state, action) => {
      if (action.payload.type === 'rate') {
        state.tip = 0;
        state.tipType = 'rate';
        state.tiprate = action.payload.value;
      } else {
        state.tiprate = 0;
        state.tipType = 'custom';
        state.tip = action.payload.value;
      }
      state.pricesStale = true;
    },
    setGuest: (state, action) => {
      state.name = action.payload.fullname;
      state.phone = action.payload.phone;
    },
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    },
    setTable: (state, action) => {
      state.table = action.payload;
    },
    setTableAlert: (state, action) => {
      state.tableAlert = action.payload;
    },
    setSchedulingType: (state, action) => {
      state.schedulingType = action.payload;
    },
    setPickupTimeUtc: (state, action) => {
      state.pickuptimeutc = action.payload;
    },
  },
});

export const {
  refreshBasket,
  addToCart,
  decrementQty,
  incrementQty,
  updateItem,
  removeItem,
  setDeliveryType,
  setDiscountCode,
  setBasketPrices,
  setPricesSettled,
  setNote,
  setTip,
  setGuest,
  setCurrentOrder,
  setBasketStore,
  setSearch,
  setTable,
  setTableAlert,
  setSchedulingType,
  setPickupTimeUtc,
} = basketSlice.actions;

export default basketSlice.reducer;
